import React, { useState } from 'react';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { Textarea } from '@/components/common/Textarea';
import { useAuthStore } from '@/store/authStore';
import { formatDistanceToNow } from 'date-fns';
import { Send, MoreHorizontal, ArrowUp } from 'lucide-react';

interface Comment {
  id: string;
  user: string;
  content: string;
  createdAt: string;
  status?: string;
}

interface CommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId?: number|string;
}

export const CommentModal: React.FC<CommentModalProps> = ({ isOpen, onClose, taskId }) => {
  const { user } = useAuthStore();
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState<Comment[]>([
    {
      id: '1',
      user: 'Vatsal Patel',
      content: 'Acknowledgement uploaded and emailed to the client at 3:15 PM. CC\'d Arpit for reference.',
      createdAt: '2024-01-15T10:30:00',
    },
    {
      id: '2',
      user: 'Ankit Gupta',
      content: 'Client requested a physical copy as well — courier scheduled via DTDC, tracking ID shared.',
      createdAt: '2024-01-15T11:45:00',
    },
    {
      id: '3',
      user: 'Vatsal Patel',
      content: 'Double-checked ITR-V before sending. Confirmed PAN and assessment year were correct.',
      createdAt: '2024-01-15T12:00:00',
    },
  ]);

  const handleSubmit = () => {
    if (comment.trim()) {
      const newComment: Comment = {
        id: Date.now().toString(),
        user: user?.name || 'Unknown User',
        content: comment,
        createdAt: new Date().toISOString(),
      };
      setComments([...comments, newComment]);
      setComment('');
    }
  };

  // Function to get avatar color based on user name
  const getAvatarColor = (userName: string) => {
    const colors = [
      'bg-teal-500',
      'bg-purple-500',
      'bg-blue-500',
      'bg-green-500',
      'bg-red-500',
      'bg-indigo-500',
      'bg-yellow-500',
      'bg-pink-500',
    ];
    const index = userName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  // Function to get user initials
  const getUserInitials = (userName: string) => {
    return userName.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Comment" size="sm">
      <div className="flex flex-col h-[350px] ">
        {/* Comments List */}
        <div className="flex-1 overflow-y-auto p-1 space-y-4 mb-4 px-4">
          {comments.map((commentItem) => (
            <div key={commentItem.id} className="flex space-x-3 group">
              {/* Avatar */}
              <div className={`w-10 h-10 ${getAvatarColor(commentItem.user)} rounded-full flex items-center justify-center text-white font-medium text-sm flex-shrink-0`}>
                {getUserInitials(commentItem.user)}
              </div>
              
              {/* Comment Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="flex justify-between items-center space-x-2">
                    <span className="font-medium text-gray-900 text-sm">
                      {commentItem.user}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(commentItem.createdAt), { addSuffix: true })}
                    </span>

                  </div>
                  <button className="p-1 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                    <MoreHorizontal size={16} className="text-gray-400" />
                  </button>
                </div>
                <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                  {commentItem.content}
                </p>
                {commentItem.status && (
                  <span className="inline-block mt-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    {commentItem.status}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Comment Input */}
        <div className="border-t border-gray-200 pt-4">
          <div className='px-4'>
          <div className="relative">
            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Enter comment..."
              className="w-full pl-4 pr-12 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            <button
              onClick={handleSubmit}
              disabled={!comment.trim()}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 bg-[#2B80FF] text-white rounded-full hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowUp  size={12}/>
              {/* <Send size={16} /> */}
            </button>
          </div>
        </div>
        </div>
      </div>
    </Modal>
  );
};